import {
  FC,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { queryCache } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { Tree } from 'antd';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { StateDataNode } from '../LeftSideView';
import { useShares } from '~src/hooks/api-hooks/useShares';
import { useProjectList } from '~src/hooks/api-hooks/useProjectList';
import { useStyles } from '~src/hooks/useStyles';
import { DirectoryTreeProps, EventDataNode } from 'antd/lib/tree';
import { appLog } from '~src/utils/log';
import SidebarContext from '~src/contexts/SidebarContext';

import FileTenderHammerSrc from '~src/assets/nav-icons/file-tender-hammer.svg';
// Primary-/Secondary Share Keys: plain (no hammer) icon for Secondary-Share-Key
// tender entries, to visually differentiate them from Primary-Share-Key ones.
import FileTenderSrc from '~src/assets/nav-icons/file-tender.svg';
import IconButton from '~components/_common/IconButton/IconButton';
import ProjectSrc from '~src/assets/nav-icons/project.png';
import TenderHammerSrc from '~src/assets/nav-icons/folder-tender.svg';
import FolderTenderHammerSrc from '~src/assets/nav-icons/folder-tender-hammer.svg';
import { styleFn } from '../style';
import {
  HomeMenuStateDataNode,
  useDefaultHomeLeftNavigationItems,
} from './constants';
import { BaseHomeNavigationKeys } from '~src/data/navigationItems';
import { useIsPath } from '~src/hooks/useIsPath';
import { DbTenderFolder } from '~src/types/db';
import {
  loadSharedTenderFolders,
  loadSharedTenderSubfolders,
  loadTenderShareCoreFolders,
  loadTendersSubFoldersShare,
} from '~src/services/api/shares';
import { changeKeysWithoutRepeat } from '../utils';
import CachedSharesContext from '~src/contexts/CachedSharesContext';
import { DB_STATE } from '~src/types/enums';
import { useMinimizedTreeData } from '~src/hooks/utils/useMinimizedTreeData';
import { isShareKeyAccessible } from '~src/utils/shareUtils/useShareKeysStatusUtils';
import {
  isUserAuthenticated,
  useIsUserAuthenticated,
} from '~src/hooks/api-hooks/useUserSettings';
import CachedPublicTenderContext from '~src/contexts/CachedPublicTender';

const { DirectoryTree } = Tree;

const HomeLeftSideView: FC = () => {
  const { isSidebarMinimized } = useContext(SidebarContext);
  const styles = useStyles(styleFn);
  const history = useHistory();
  const location = useLocation();

  const isOnProjectListCachedTenders = useIsPath('invitations');
  const isOnProjectListPublicTenders = useIsPath('public-tenders');
  const isOnProjectListMyTenders = useIsPath('my-tenders');
  const isOnProjectList = useIsPath('projectId');

  // const [shareTendersList, setShareTendersList] = useState<DbTenderShare[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const navigationList = useDefaultHomeLeftNavigationItems(styles);
  const [treeData, setTreeData] = useState<HomeMenuStateDataNode[]>(
    navigationList
  );
  const isAuthenticated = useIsUserAuthenticated();

  useEffect(() => {
    setTreeData((prevTreeData) =>
      prevTreeData.map((node) => {
        if (
          node.key === BaseHomeNavigationKeys.PROJECTWEB ||
          node.key === BaseHomeNavigationKeys.MY_TENDERS
        ) {
          return {
            ...node,
            disabled: !isAuthenticated,
          };
        }
        return node;
      })
    );
  }, [isAuthenticated]);

  const {
    cachedSharesContext,
    currentCachedShare,
    setCachedSharesContext,
  } = useContext(CachedSharesContext);

  const {
    cachedPublicTendersContext,
    currentCachedPublicTender,
    setCachedPublicTendersContext,
  } = useContext(CachedPublicTenderContext);

  const projectsQueryResult = useProjectList({
    refetchOnMount: false,
  });
  const sharesQueryData = useShares({});

  const sharesListWithoutRemoved = useMemo(
    () =>
      sharesQueryData.data?.filter(
        (s) => s?.key?.share_key_state !== DB_STATE.REJECTED
      ),
    [sharesQueryData.data]
  );

  // console.log(`cache-shares context`, {
  //   cachedSharesContext,
  //   currentCachedShare,
  //   setCachedSharesContext,
  // });

  useEffect(() => {
    const cachedSharesFromLocalStorage = JSON.parse(
      localStorage.getItem('shares') || '[]'
    );

    setCachedSharesContext(cachedSharesFromLocalStorage);
  }, [setCachedSharesContext]);

  useEffect(() => {
    const cachedPublicTendersFromLocalStorage = JSON.parse(
      localStorage.getItem('publicTenders') || '[]'
    );

    setCachedPublicTendersContext(cachedPublicTendersFromLocalStorage);
  }, [setCachedPublicTendersContext]);

  useEffect(() => {
    setTreeData((prevTreeData) => {
      const newTreeData = [...prevTreeData];

      const cachesSharesTreeNode = newTreeData.find(
        (node) => node.key === BaseHomeNavigationKeys.CACHED_SHARES
      );

      if (!cachesSharesTreeNode) return newTreeData;

      // Always update children with the latest cached shares context
      cachesSharesTreeNode.children = cachedSharesContext.map((share) => ({
        title: share.title,
        key: `${BaseHomeNavigationKeys.CACHED_SHARES}_${share.shareKey}`,
        path: `/invitations/${share.shareId}-${share.shareKey}`,
        parentId: BaseHomeNavigationKeys.CACHED_SHARES,
        icon: <NavIcon icon={FileTenderHammerSrc} />,
        isLeaf: true,
      }));

      return newTreeData;
    });
  }, [cachedSharesContext]);

  useEffect(() => {
    setTreeData((prevTreeData) => {
      const newTreeData = [...prevTreeData];

      const cachedPublicTendersTreeNode = newTreeData.find(
        (node) => node.key === BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS
      );

      if (!cachedPublicTendersTreeNode) return newTreeData;

      cachedPublicTendersTreeNode.children = cachedPublicTendersContext.map(
        (publicTender) => ({
          title: publicTender.title || publicTender.tenderShareId,
          key: `${BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS}_${publicTender.tenderShareId}`,
          path: `/public-tenders/${publicTender.tenderShareId}`,
          parentId: BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS,
          icon: <NavIcon icon={FileTenderHammerSrc} />,
          isLeaf: true,
        })
      );

      return newTreeData;
    });
  }, [cachedPublicTendersContext]);

  useEffect(() => {
    setTreeData((prevTreeData) => {
      const newTreeData = [...prevTreeData];

      const myTendersMenuTreeNode = newTreeData.find(
        (node) => node.key === BaseHomeNavigationKeys.MY_TENDERS
      );

      if (!myTendersMenuTreeNode) return newTreeData;

      myTendersMenuTreeNode.isLeaf = !sharesListWithoutRemoved?.length;

      // IMPORTANT:
      // Always rebuild children, because we can have same share_id with different share_key_id
      // and we need each entry to be unique and independently selectable.
      myTendersMenuTreeNode.children = sharesListWithoutRemoved?.map(
        (share) => {
          const displayTitle =
            // share?.share_title ||
            // share?.key?.share_key_title ||
            share?.registration?.registration_title ||
            // share?.project?.name ||
            'Unknown';

          const shareId = String(share.share_id);
          const shareKeyId = share?.key?.share_key_id || undefined;
          const shareIdKey = `${shareId}-${shareKeyId ?? 'nokey'}`;
          // Primary-/Secondary Share Keys: differentiate the two with a
          // distinct icon - plain file icon for a Secondary-Share-Key,
          // hammer icon (existing default) for a Primary-Share-Key.
          const isSecondaryShareKey =
            share?.key?.share_key_is_primary === false;

          // Primary-/Secondary Share Keys: block opening a child share whose
          // parent inviter has been rejected/revoked, or whose own key is
          // removed/deleted/expired - mirrors backend's access-check logic.
          const isAccessible = isShareKeyAccessible(share?.key);

          return {
            ...share,
            id: shareId,
            title: displayTitle,

            // IMPORTANT:
            // Use /id-key so routing can distinguish duplicates share_id.
            path: `/my-tenders/${shareIdKey}`,

            key: `${BaseHomeNavigationKeys.MY_TENDERS}_${shareId}_${
              shareKeyId ?? 'nokey'
            }`,
            keyPath: shareId,
            icon: (
              <NavIcon
                icon={isSecondaryShareKey ? FileTenderSrc : FileTenderHammerSrc}
                customStyle={{ opacity: isAccessible ? 1 : 0.35 }}
              />
            ),
            parentId: BaseHomeNavigationKeys.MY_TENDERS,
            meta: {
              share_id: shareId,
              share_key: shareKeyId,
              share_id_key: shareIdKey,
            },
            children: [],
            // Blocked shares get no expand arrow: `disabled` alone only
            // blocks onSelect/checkbox, not the expand switcher, and
            // handleLoadNextTreeData would still fetch (and 403 on) this
            // share's subfolders if left expandable.
            isLeaf: !isAccessible,
            disabled: !isAccessible,
          };
        }
      );

      return newTreeData;
    });
  }, [sharesListWithoutRemoved]);

  useEffect(() => {
    setTreeData((prevTreeData) => {
      const newTreeData = [...prevTreeData];

      const projectWebTreeNode = newTreeData.find(
        (node) => node.key === BaseHomeNavigationKeys.PROJECTWEB
      );

      if (!projectWebTreeNode) return newTreeData;

      if (!projectWebTreeNode.children?.length) {
        projectWebTreeNode.children = projectsQueryResult.data?.map(
          (project) => ({
            id: project.id,
            title: project.name,
            path: `/my-projects/${project.id}`,
            key: `${BaseHomeNavigationKeys.PROJECTWEB}_${project.id}`,
            keyPath: String(project.id),
            icon: <NavIcon icon={ProjectSrc} />,
            parentId: BaseHomeNavigationKeys.PROJECTWEB,
            children: [],
            isLeaf: true,
          })
        );
      }

      return newTreeData;
    });
  }, [projectsQueryResult.data]);

  // Compute which items should be visible based on data availability
  const hasProjects =
    projectsQueryResult.data && projectsQueryResult.data.length > 0;
  const hasMyTenders =
    sharesQueryData.data &&
    sharesQueryData.data.some((share: any) => share?.module === 15);
  const hasInvitations = cachedSharesContext && cachedSharesContext.length > 0;
  const hasPublicTenders =
    cachedPublicTendersContext && cachedPublicTendersContext.length > 0;

  // Filter tree data to only show items with data
  const visibleTreeData = useMemo(() => {
    // console.log('🔍 [DEBUG-OTHER-FEATURES] Navigation Filter:', {
    //   hasProjects,
    //   hasMyTenders,
    //   hasInvitations,
    //   isAuthenticated,
    //   currentTreeDataKeys: treeData.map((n) => n.key),
    // });

    return treeData.filter((node) => {
      // Keep invitations only if has cached invitations
      if (node.key === BaseHomeNavigationKeys.CACHED_SHARES) {
        return hasInvitations;
      }

      if (node.key === BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS) {
        return hasPublicTenders;
      }

      // Keep projectweb only if has projects
      if (node.key === BaseHomeNavigationKeys.PROJECTWEB) {
        return hasProjects;
      }
      // Keep my-tenders only if has tenders with module 15
      if (node.key === BaseHomeNavigationKeys.MY_TENDERS) {
        return hasMyTenders;
      }
      return true;
    });
  }, [
    treeData,
    hasProjects,
    hasMyTenders,
    hasInvitations,
    hasPublicTenders,
    isAuthenticated,
  ]);

  const handleSelect: DirectoryTreeProps['onSelect'] = (
    _,
    info: {
      event: 'select';
      selected: boolean;
      node: EventDataNode | StateDataNode;
    }
  ) => {
    if (!('path' in info.node) || !info.node?.path) {
      appLog("no 'path' in selected node, node:", info.node);
      return;
    }

    // Block navigation to My Projects / My Tenders for unauthenticated users
    if (
      !isAuthenticated &&
      (info.node.key === BaseHomeNavigationKeys.PROJECTWEB ||
        info.node.key === BaseHomeNavigationKeys.MY_TENDERS ||
        info.node.parentId === BaseHomeNavigationKeys.PROJECTWEB ||
        info.node.parentId === BaseHomeNavigationKeys.MY_TENDERS)
    ) {
      return;
    }

    return history.push(info.node.path);
  };

  useEffect(() => {
    const locationList = location.pathname.split('/').filter(Boolean);

    if (isOnProjectListMyTenders && sharesQueryData.data) {
      // New URL format:
      // /my-tenders/:shareIdKey/...
      const shareIdKeyFromUrl = locationList[1];
      const [tenderId, shareKeyFromUrl] = String(shareIdKeyFromUrl || '').split(
        '-'
      );

      const matchingShare = sharesQueryData.data.find(
        ({ share_id, key }) =>
          String(share_id) === String(tenderId) &&
          String(key?.share_key_id ?? '') === String(shareKeyFromUrl ?? '')
      );

      // Backward compatibility (old /my-tenders/:id)
      const firstMatchingKey = matchingShare
        ? matchingShare
        : sharesQueryData.data.find(({ share_id }) => share_id === tenderId);

      if (!tenderId) {
        setExpandedKeys((prevExpandedKeys) => {
          return changeKeysWithoutRepeat({
            changedKey: BaseHomeNavigationKeys.MY_TENDERS,
            prevKeys: prevExpandedKeys,
          });
        });
        return setSelectedKeys([BaseHomeNavigationKeys.MY_TENDERS]);
      }

      const resolvedShareKeyId =
        shareKeyFromUrl || firstMatchingKey?.key?.share_key_id;

      if (!resolvedShareKeyId) {
        appLog('[HomeLeftSideView] missing share_key_id in url and data', {
          tenderId,
          shareIdKeyFromUrl,
          firstMatchingKey,
        });
        setExpandedKeys((prevExpandedKeys) =>
          changeKeysWithoutRepeat({
            changedKey: BaseHomeNavigationKeys.MY_TENDERS,
            prevKeys: prevExpandedKeys,
          })
        );
        return setSelectedKeys([BaseHomeNavigationKeys.MY_TENDERS]);
      }

      const currentTenderKey = `${BaseHomeNavigationKeys.MY_TENDERS}_${tenderId}_${resolvedShareKeyId}`;

      if (locationList.includes('folders')) {
        // Robust parse (no magic indexes):
        // /my-tenders/:shareIdKey/tender-folders/:rootId/folders/:folderId/...
        const tenderFoldersIndex = locationList.indexOf('tender-folders');
        const foldersIndex = locationList.indexOf('folders');

        const rootFolderId =
          tenderFoldersIndex !== -1
            ? locationList[tenderFoldersIndex + 1]
            : undefined;

        const folderId =
          foldersIndex !== -1 ? locationList[foldersIndex + 1] : undefined;

        if (!rootFolderId || !folderId) {
          appLog('[HomeLeftSideView] cannot parse folders route', {
            locationList,
            tenderFoldersIndex,
            foldersIndex,
            rootFolderId,
            folderId,
          });

          setExpandedKeys((prevExpandedKeys) =>
            changeKeysWithoutRepeat({
              changedKey: BaseHomeNavigationKeys.MY_TENDERS,
              prevKeys: prevExpandedKeys,
            })
          );
          return setSelectedKeys([BaseHomeNavigationKeys.MY_TENDERS]);
        }

        const rootFolderKey = `${currentTenderKey}_folder_${rootFolderId}`;
        const currentFolderKey = `${currentTenderKey}_folder_${folderId}`;

        setExpandedKeys((prevExpandedKeys) => {
          const newExpandedKeys = [
            ...prevExpandedKeys,
            BaseHomeNavigationKeys.MY_TENDERS,
            currentTenderKey,
          ];

          if (rootFolderKey !== currentFolderKey) {
            newExpandedKeys.push(rootFolderKey);
          }

          newExpandedKeys.push(currentFolderKey);

          return changeKeysWithoutRepeat({
            newKeys: newExpandedKeys,
            changedKey: currentFolderKey,
            prevKeys: prevExpandedKeys,
          });
        });

        return setSelectedKeys([currentFolderKey]);
      }

      setExpandedKeys((prevExpandedKeys) => {
        return changeKeysWithoutRepeat({
          newKeys: [BaseHomeNavigationKeys.MY_TENDERS, currentTenderKey],
          changedKey: currentTenderKey,
          prevKeys: prevExpandedKeys,
        });
      });

      return setSelectedKeys([currentTenderKey]);
    }
    if (isOnProjectListCachedTenders) {
      setExpandedKeys((prevExpandedKeys) =>
        changeKeysWithoutRepeat({
          changedKey: BaseHomeNavigationKeys.CACHED_SHARES,
          prevKeys: prevExpandedKeys,
        })
      );

      const invitationIdKeyFromUrl = locationList[1] || '';
      const [, shareKeyFromUrl] = invitationIdKeyFromUrl.split('-');
      const resolvedShareKey = shareKeyFromUrl || currentCachedShare?.shareKey;

      if (resolvedShareKey && locationList.length > 1) {
        const formattedKey = `${BaseHomeNavigationKeys.CACHED_SHARES}_${resolvedShareKey}`;
        return setSelectedKeys([formattedKey]);
      }

      return setSelectedKeys([BaseHomeNavigationKeys.CACHED_SHARES]);
    }
    if (isOnProjectListPublicTenders) {
      setExpandedKeys((prevExpandedKeys) =>
        changeKeysWithoutRepeat({
          changedKey: BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS,
          prevKeys: prevExpandedKeys,
        })
      );

      const tenderShareIdFromUrl = locationList[1] || '';
      const resolvedTenderShareId =
        tenderShareIdFromUrl || currentCachedPublicTender?.tenderShareId;

      if (resolvedTenderShareId && locationList.length > 1) {
        const formattedKey = `${BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS}_${resolvedTenderShareId}`;
        return setSelectedKeys([formattedKey]);
      }

      return setSelectedKeys([BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS]);
    }
    if (!isOnProjectList) {
      setSelectedKeys([BaseHomeNavigationKeys.HOME]);
    }
    if (isOnProjectList && isAuthenticated) {
      const projectId = String(location.pathname.split('/').pop());
      const selectedProjectKey = `${BaseHomeNavigationKeys.PROJECTWEB}_${projectId}`;
      setExpandedKeys((prevExpanded) =>
        changeKeysWithoutRepeat({
          changedKey: BaseHomeNavigationKeys.PROJECTWEB,
          prevKeys: prevExpanded,
        })
      );
      setSelectedKeys([selectedProjectKey]);
    } else if (!isOnProjectList) {
      setSelectedKeys([BaseHomeNavigationKeys.HOME]);
    }
  }, [
    sharesQueryData.data,
    location.pathname,
    treeData,
    isOnProjectList,
    isOnProjectListCachedTenders,
    isOnProjectListMyTenders,
    currentCachedShare,
    isOnProjectListPublicTenders,
    currentCachedPublicTender,
    isAuthenticated,
  ]);

  const handleLoadNextTreeData = async (node: HomeMenuStateDataNode) => {
    const nodeKey = String(node.key);

    if (nodeKey === BaseHomeNavigationKeys.CACHED_SHARES) return;
    if (nodeKey === BaseHomeNavigationKeys.CACHED_PUBLIC_TENDERS) return;
    if (nodeKey === BaseHomeNavigationKeys.PROJECTWEB) return;
    if (nodeKey === BaseHomeNavigationKeys.MY_TENDERS) return;

    // IMPORTANT:
    // Do NOT block folder nodes here, otherwise subfolders will never load.
    // (previous guard "if (node.parentId?.includes('folder')) return;" was breaking subfolder loading)

    if (nodeKey.includes(`${BaseHomeNavigationKeys.MY_TENDERS}_`)) {
      // Handle tender folders loading (folder node)
      const isFolderNode = nodeKey.includes(`_folder_`);
      if (isFolderNode) {
        const folderId = nodeKey.split(`_folder_`)[1];

        const rootFolderIdFromMeta =
          (node.meta as any)?.root_folder_id || folderId;

        const sharedTenderSubFoldersArgs = {
          share_id: node.meta?.share_id as string,
          share_key: node.meta?.share_key,
          root_folder_id: folderId,
        };

        let unstructuredSharedTenderSubFolders: DbTenderFolder[] =
          queryCache.getQueryData<DbTenderFolder[]>([
            'sharedTenderSubFolders',
            sharedTenderSubFoldersArgs,
          ]) || [];

        if (unstructuredSharedTenderSubFolders.length === 0) {
          unstructuredSharedTenderSubFolders = await loadTendersSubFoldersShare(
            {
              id: sharedTenderSubFoldersArgs.share_id,
              key: sharedTenderSubFoldersArgs.share_key,
              rootFolderId: sharedTenderSubFoldersArgs.root_folder_id,
            }
          );
          queryCache.setQueryData(
            ['sharedTenderSubFolders', sharedTenderSubFoldersArgs],
            unstructuredSharedTenderSubFolders
          );
        }

        return setTreeData((prevTreeData) => {
          const newTreeData = [...prevTreeData];

          const currentTenderKey = `${BaseHomeNavigationKeys.MY_TENDERS}_${sharedTenderSubFoldersArgs.share_id}_${sharedTenderSubFoldersArgs.share_key}`;
          const currentTenderFolderKey = `${currentTenderKey}_folder_${folderId}`;

          const currentTenderFolderTreeNode = getMyTendersTreeNode(newTreeData)
            .children?.find(({ key }) => key === currentTenderKey)
            ?.children?.find(({ key }) => key === currentTenderFolderKey);

          const subFoldersTreeData = parseFoldersRecursively(
            unstructuredSharedTenderSubFolders,
            unstructuredSharedTenderSubFolders[0],
            sharedTenderSubFoldersArgs.share_id,
            sharedTenderSubFoldersArgs.share_key,
            rootFolderIdFromMeta
          );

          if (currentTenderFolderTreeNode)
            currentTenderFolderTreeNode.children = subFoldersTreeData;

          return newTreeData;
        });
      }

      // Handle tender first level folders loading (tender node)
      const keyParts = nodeKey.split(`_`);
      const tenderId = keyParts[1];
      const shareKeyFromNodeKey = keyParts[2];

      const sharedTenderFoldersArgs = {
        share_id: String(tenderId),
        share_key: node.meta?.share_key ?? shareKeyFromNodeKey,
      };

      if (!sharedTenderFoldersArgs.share_key) {
        appLog('[HomeLeftSideView] missing share_key for core_folders', {
          nodeKey,
          meta: node.meta,
          sharedTenderFoldersArgs,
        });
        return;
      }

      let sharedTenderFolders = queryCache.getQueryData<DbTenderFolder[]>([
        'sharedTenderFolders',
        sharedTenderFoldersArgs,
      ]);

      if (!sharedTenderFolders) {
        // console.log(`folder error #1`, {
        //   id: sharedTenderFoldersArgs.share_id,
        //   key: sharedTenderFoldersArgs.share_key,
        // });
        sharedTenderFolders = await loadTenderShareCoreFolders({
          id: sharedTenderFoldersArgs.share_id,
          key: sharedTenderFoldersArgs.share_key,
        });
        queryCache.setQueryData(
          ['sharedTenderFolders', sharedTenderFoldersArgs],
          sharedTenderFolders
        );
      }

      return setTreeData((prevTreeData) => {
        const newTreeData = [...prevTreeData];

        const currentTenderKey = `${BaseHomeNavigationKeys.MY_TENDERS}_${tenderId}_${sharedTenderFoldersArgs.share_key}`;

        const currentTenderTreeNode = getMyTendersTreeNode(
          newTreeData
        ).children?.find(({ key }) => key === currentTenderKey);

        if (currentTenderTreeNode) {
          const shareIdKey = `${tenderId}-${sharedTenderFoldersArgs.share_key}`;

          currentTenderTreeNode.children = sharedTenderFolders?.map(
            (folder) => {
              const folderKey = `${currentTenderKey}_folder_${folder.id}`;

              return {
                id: folder.id,
                title: folder?.name,
                path: `/my-tenders/${shareIdKey}/tender-folders/${folder.id}/folders/${folder.id}/documents`,
                key: folderKey,
                keyPath: String(folder.id),
                icon: <NavIcon icon={FolderTenderHammerSrc} />,
                parentId: currentTenderKey,
                meta: {
                  ...sharedTenderFoldersArgs,
                  root_folder_id: folder.id,
                },
                children: [],
                isLeaf: false,
              };
            }
          );
        }

        return newTreeData;
      });
    }
  };

  const timeOutRef = useRef<number>();
  const LONG_PRESS_TIME = 6 * 100;

  const minimizedTreeData = useMinimizedTreeData(
    isSidebarMinimized,
    visibleTreeData
  );

  const adjustedTreeData = useMemo(
    () =>
      isAuthenticated
        ? minimizedTreeData
        : minimizedTreeData.filter(
            (node) =>
              node.key !== BaseHomeNavigationKeys.PROJECTWEB &&
              node.key !== BaseHomeNavigationKeys.MY_TENDERS
          ),
    [minimizedTreeData, isAuthenticated]
  );

  return (
    <Fragment>
      <div
        css={styles.container}
        onContextMenu={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}>
        <DirectoryTree
          className={`
              draggable-tree
              ${isSidebarMinimized ? 'composited-tree' : ''}
            `}
          blockNode
          treeData={adjustedTreeData}
          loadData={handleLoadNextTreeData}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          onSelect={handleSelect}
          onExpand={(keys) => setExpandedKeys(keys as string[])}
          onActiveChange={(key) => {
            appLog('key here -> ', key);
          }}
          titleRender={(node: StateDataNode) => (
            <span
              onTouchStart={(event) => {
                event.preventDefault();
                timeOutRef.current = window.setTimeout(() => {
                  timeOutRef.current = undefined;
                }, LONG_PRESS_TIME);
              }}
              onTouchEnd={(e) => {
                window.clearTimeout(timeOutRef.current);
              }}>
              {isSidebarMinimized ? null : node.title}
              {node.favourite && (
                <IconButton
                  customStyle={{ fontSize: 12, marginBottom: 1 }}
                  containerStyle={{ minHeight: 20, minWidth: 20 }}
                  icon={['fas', 'star']}
                  color="yellow"
                  backgroundOff
                />
              )}
            </span>
          )}
        />
      </div>
    </Fragment>
  );
};

function getMyTendersTreeNode<TreeDataT extends StateDataNode>(
  treeData: TreeDataT[]
): TreeDataT {
  return treeData[3];
}

function parseFoldersRecursively(
  data: DbTenderFolder[],
  parent: DbTenderFolder | null,
  tenderId?: string,
  shareKeyId?: string,
  rootFolderId?: string
): any[] {
  return data
    .filter((item) => item.parent_id === (parent ? parent.id : null))
    .map((item) => {
      const children = parseFoldersRecursively(
        data,
        item,
        tenderId,
        shareKeyId,
        rootFolderId
      );

      const currentTenderKey = `${BaseHomeNavigationKeys.MY_TENDERS}_${tenderId}_${shareKeyId}`;
      const folderKey = `${currentTenderKey}_folder_${item.id}`;
      const shareIdKey = `${tenderId}-${shareKeyId}`;

      return {
        id: item.id,
        title: item.name,
        path: `/my-tenders/${shareIdKey}/tender-folders/${rootFolderId}/folders/${item.id}/documents`,
        key: folderKey,
        keyPath: String(item.id),
        icon: <NavIcon icon={TenderHammerSrc} />,
        parentId: folderKey,
        meta: {
          share_id: tenderId,
          share_key: shareKeyId,
          root_folder_id: rootFolderId,
        },
        children,
        firstElementId: rootFolderId,
        isLeaf: children.length === 0,
      };
    });
}

export default HomeLeftSideView;
